<template>
    <PanelFrame ref="panelFrame" panel-class="weather-app" resize-directions="all" :content-class="panelContentClass">
        <template #titlebar-left>
            <button v-if="isCompact && mobileView === 'content'" class="panel-titlebar-btn" @click.stop="mobileView = 'list'">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button v-else class="panel-titlebar-btn" @click.stop="sidebarOpen = !sidebarOpen" :title="$t('common.weather.sidebar')">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="2" width="20" height="20" rx="2" ry="2"/><line x1="9" y1="2" x2="9" y2="22"/>
                </svg>
            </button>
            <span class="weather-titlebar-city">{{ titlebarCity }}</span>
        </template>

        <template #titlebar-actions>
            <button class="panel-titlebar-btn" @click.stop="onLoadDemo" title="Demo">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            </button>
            <button class="panel-titlebar-btn" @click.stop="onRefresh" :title="$t('common.weather.refresh')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            </button>
        </template>

        <template #default>
            <div class="weather-panel-fx" v-if="animationsEnabled && (!isCompact || mobileView === 'content')">
                <template v-if="panelEffect === 'sunny'"></template>

                <template v-else-if="panelEffect === 'partcloud'">
                    <span v-for="i in 5" :key="'pc'+i" class="wp-fx-cloud" :style="cloudStyle(i)"></span>
                </template>

                <template v-else-if="panelEffect === 'cloud'">
                    <span v-for="i in 7" :key="'c'+i" class="wp-fx-cloud wp-fx-cloud--dense" :style="cloudStyle(i)"></span>
                </template>

                <template v-else-if="panelEffect === 'rain'">
                    <span v-for="i in 50" :key="'r'+i" class="wp-fx-drop" :style="rainStyle(i)"></span>
                </template>

                <template v-else-if="panelEffect === 'thunder'">
                    <span v-for="i in 60" :key="'r'+i" class="wp-fx-drop wp-fx-drop--heavy" :style="rainStyle(i)"></span>
                    <span class="wp-fx-flash"></span>
                    <span class="wp-fx-bolt"></span>
                    <span class="wp-fx-bolt wp-fx-bolt--2"></span>
                </template>

                <template v-else-if="panelEffect === 'snow'">
                    <span v-for="i in 40" :key="'s'+i" class="wp-fx-flake" :style="snowStyle(i)"></span>
                </template>

                <template v-else-if="panelEffect === 'fog'">
                    <span v-for="i in 4" :key="'f'+i" class="wp-fx-fog" :style="fogStyle(i)"></span>
                </template>

                <template v-else-if="panelEffect === 'night'">
                    <span v-for="i in 30" :key="'st'+i" class="wp-fx-star" :style="starStyle(i)"></span>
                </template>

                <template v-else-if="panelEffect === 'night_few'">
                    <span v-for="i in 28" :key="'st'+i" class="wp-fx-star" :style="starStyle(i)"></span>
                    <span v-for="i in 2" :key="'nc'+i" class="wp-fx-cloud wp-fx-cloud--night" :style="cloudStyle(i)"></span>
                </template>

                <template v-else-if="panelEffect === 'night_partcloud'">
                    <span v-for="i in 22" :key="'st'+i" class="wp-fx-star" :style="starStyle(i)"></span>
                    <span v-for="i in 4" :key="'nc'+i" class="wp-fx-cloud wp-fx-cloud--night" :style="cloudStyle(i)"></span>
                </template>

                <template v-else-if="panelEffect === 'night_cloud'">
                    <span v-for="i in 12" :key="'st'+i" class="wp-fx-star" :style="starStyle(i)"></span>
                    <span v-for="i in 7" :key="'nc'+i" class="wp-fx-cloud wp-fx-cloud--night wp-fx-cloud--dense" :style="cloudStyle(i)"></span>
                </template>

                <div v-if="accumulationEnabled && (panelEffect === 'rain' || panelEffect === 'thunder')" class="wp-fx-puddle" :class="{'wp-fx-puddle--thunder': panelEffect === 'thunder'}" :key="'puddle-' + ws.state.activeIndex">
                    <span v-for="i in 8" :key="'rp'+i" class="wp-fx-ripple" :style="rippleStyle(i)"></span>
                </div>

                <div v-if="accumulationEnabled && panelEffect === 'snow'" class="wp-fx-snow-ground" :key="'snow-' + ws.state.activeIndex">
                    <span v-for="i in 10" :key="'sm'+i" class="wp-fx-snow-mound" :style="snowMoundStyle(i)"></span>
                </div>
            </div>
            <span v-if="animationsEnabled && panelEffect === 'sunny' && (!isCompact || mobileView === 'content')" class="wp-fx-sun-wrap">
                <span class="wp-fx-sun-glow"></span>
                <span class="wp-fx-sun-rays">
                    <span v-for="i in 10" :key="'ray'+i" class="wp-fx-sun-ray" :style="{transform: 'rotate(' + (i * 36) + 'deg)'}"></span>
                </span>
                <span class="wp-fx-sun-disc"></span>
            </span>

            <div class="weather-body">
                <WeatherSidebar v-if="isCompact ? mobileView === 'list' : sidebarOpen"
                    :width="isCompact ? null : sidebarWidth"
                    :active-index="ws.state.activeIndex"
                    :animations-enabled="animationsEnabled"
                    @select="onLocationSelect"
                    @divider-start="startSidebarResize"/>
                <WeatherContent ref="weatherContent" v-show="!isCompact || mobileView === 'content'"/>
                <div v-if="!ws.state._ready && !ws.state.locations.length" class="weather-loading">
                    <span class="weather-loading-spinner"></span>
                    <span class="weather-loading-text">{{ $t('common.weather.loading') }}</span>
                </div>
                <div v-else-if="ws.state._ready && !ws.state.locations.length" class="weather-onboarding">
                    <div class="weather-onboarding-bg">
                        <span class="weather-onboarding-orb weather-onboarding-orb--1"></span>
                        <span class="weather-onboarding-orb weather-onboarding-orb--2"></span>
                    </div>
                    <div class="weather-onboarding-inner">
                        <div class="weather-onboarding-icon">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                        </div>
                        <div class="weather-onboarding-title">{{ $t('common.weather.title') }}</div>
                        <div class="weather-onboarding-hint">{{ $t('common.weather.noLocations') }}</div>
                        <WeatherCitySearch @select="onOnboardingCity"/>
                        <div class="weather-onboarding-divider"><span></span><span class="weather-onboarding-divider-text">veya</span><span></span></div>
                        <button class="weather-onboarding-detect" @click="onDetect">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            {{ $t('common.weather.detectLocation') }}
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </PanelFrame>
</template>

<script>
import './weather-panel.css'
import { createPanelMixin } from '@/components/panel/usePanelBase'
import PanelFrame from '@/components/panel/PanelFrame.vue'
import WeatherSidebar from './WeatherSidebar.vue'
import WeatherContent from './WeatherContent.vue'
import WeatherCitySearch from './WeatherCitySearch.vue'
import weatherService from './weatherService'

const ICON_EFFECT = {
    thunderstorm: 'thunder',
    rain: 'rain', rain_shower: 'rain', freezing_rain: 'rain',
    drizzle: 'rain', freezing_drizzle: 'rain',
    snow: 'snow', snow_grains: 'snow', snow_shower: 'snow',
    fog: 'fog',
    clear: 'sunny', mainly_clear: 'sunny', night_clear: 'night',
    partly_cloudy: 'partcloud',
    overcast: 'cloud',
}

const weatherPanelMixin = createPanelMixin({
    panelId: 'weatherPanel',
    shortcut: { code: 'KeyW' },
    minWidth: 288,
    minHeight: 400,
    defaults: {
        floatingMode: false,
        width: 1030,
        height: 720,
        sidebarWidth: 280,
    },
    hasInnerLayout: true,
    showSettingsInCompact: true,
    minSidebar: 180,
    maxSidebar: 320,
    settingsItems: [
        'alwaysOnTop',
        'openOnLoad',
        'divider',
        { key: 'animationsEnabled', label: 'common.weather.animations', icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>', default: true },
        { key: 'accumulationEnabled', label: 'common.weather.accumulation', icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 17.58A5 5 0 0 0 18 8h-1.26A8 8 0 1 0 4 16.25"/><path d="M8 16h.01M8 20h.01M12 18h.01M12 22h.01M16 16h.01M16 20h.01"/></svg>', default: true, visible: vm => vm.animationsEnabled },
        'divider',
        'resetWindowSize',
        'resetInnerLayout',
    ],
    onOpen(vm) {
        weatherService.onPanelOpen()
    },
    extraStorageKeys(vm) {
        return { sidebarOpen: vm.sidebarOpen }
    },
})

export default {
    name: 'WeatherPanel',
    mixins: [weatherPanelMixin],
    components: { PanelFrame, WeatherSidebar, WeatherContent, WeatherCitySearch },
    data() {
        const saved = this.panelLoadLayout()
        return {
            sidebarOpen: saved.sidebarOpen ?? true,
            mobileView: 'content',
        }
    },
    computed: {
        ws() {
            return weatherService
        },
        activeLoc() {
            return weatherService.activeLocation.value
        },
        daily() {
            return this.activeLoc?.data?.daily || []
        },
        isNight() {
            const today = this.daily[0]
            if (!today?.sunrise || !today?.sunset) return false
            const now = new Date()
            const [sh, sm] = today.sunrise.split(':').map(Number)
            const [eth, etm] = today.sunset.split(':').map(Number)
            const mins = now.getHours() * 60 + now.getMinutes()
            return mins < sh * 60 + sm || mins > eth * 60 + etm
        },
        panelEffect() {
            const current = this.activeLoc?.data?.current
            if (!current) return this.isNight ? 'night' : 'sunny'
            let effect
            const icon = current.condition?.icon || current.condition_icon
            if (icon && ICON_EFFECT[icon]) {
                effect = ICON_EFFECT[icon]
            } else {
                const id = Number(current.condition?.id || current.condition_id) || 0
                if (id) {
                    if ([95, 96, 99].includes(id)) effect = 'thunder'
                    else if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(id)) effect = 'rain'
                    else if ([71, 73, 75, 77, 85, 86].includes(id)) effect = 'snow'
                    else if ([45, 48].includes(id)) effect = 'fog'
                    else if ([0, 1].includes(id)) effect = 'sunny'
                    else if (id === 2) effect = 'partcloud'
                    else if (id === 3) effect = 'cloud'
                    else effect = 'sunny'
                } else {
                    const label = (current.condition?.label || '').toLowerCase()
                    if (label.includes('gök gürültü') || label.includes('thunder')) effect = 'thunder'
                    else if (label.includes('yağmur') || label.includes('rain') || label.includes('çisenti')) effect = 'rain'
                    else if (label.includes('kar') || label.includes('snow')) effect = 'snow'
                    else if (label.includes('sis') || label.includes('fog')) effect = 'fog'
                    else if (label.includes('bulut') || label.includes('cloud') || label.includes('kapalı')) effect = 'cloud'
                    else effect = 'sunny'
                }
            }
            if (this.isNight) {
                const icon = current?.condition?.icon || current?.condition_icon || ''
                if (icon === 'mainly_clear' || (effect === 'sunny' && icon !== 'clear')) return 'night_few'
                if (effect === 'sunny') return 'night'
                if (effect === 'partcloud') return 'night_partcloud'
                if (effect === 'cloud') return 'night_cloud'
            }
            return effect
        },
        contentNarrow() {
            if (this.isCompact) return false
            const sw = this.sidebarOpen ? this.sidebarWidth : 0
            return (this.panelWidth - sw) < 480
        },
        panelContentClass() {
            if (this.ws.state._ready && !this.ws.state.locations.length) return 'weather-active weather-onboarding-mode'
            let cls = 'weather-active weather-cond--' + this.panelEffect
            if (this.contentNarrow) cls += ' weather-content-narrow'
            return cls
        },
        titlebarCity() {
            if (!this.sidebarOpen) {
                const loc = this.activeLoc
                if (loc) {
                    if (loc.districtName && loc.cityName && loc.districtName !== loc.cityName) {
                        return loc.cityName + ', ' + loc.districtName
                    }

                    return loc.cityName || loc.districtName || this.$t('common.weather.title')
                }
            }

            return this.$t('common.weather.title')
        },
    },
    methods: {
        onResetInnerLayout() {
            this.sidebarWidth = 280
            this.sidebarOpen = true
            this.panelSaveLayoutDebounced()
        },
        onLocationSelect(index) {
            weatherService.setActive(index)
            if (this.isCompact) {
                this.mobileView = 'content'
                this.$nextTick(() => {
                    const el = this.$refs.weatherContent?.$refs?.scroll
                    if (el) el.scrollTop = 0
                })
            }
        },
        onLoadDemo() {
            weatherService.loadDemo()
        },
        async onRefresh() {
            await weatherService.fetchAll()
        },
        onOnboardingCity(city) {
            weatherService.addLocation(city.cityId, city.districtId, city.cityName, city.districtName)
            weatherService.setDefault(0)
        },
        async onDetect() {
            try {
                await weatherService.detectLocation()
            } catch (e) { void e }
        },
        rainStyle(i) {
            const dur = 0.55 + (i % 5) * 0.07
            return {
                left: ((i * 2.1 + (i * 7) % 13) % 100) + '%',
                animationDelay: -(dur * ((i * 0.37) % 1)) + 's',
                animationDuration: dur + 's',
            }
        },
        snowStyle(i) {
            return {
                left: ((i * 2.6 + (i * 11) % 17) % 100) + '%',
                animationDelay: ((i * 0.22) % 3) + 's',
                animationDuration: (2.5 + (i % 4) * 0.6) + 's',
            }
        },
        rayStyle(i) {
            const top = -10 + (i * 22) % 80
            const angle = -15 + (i * 7) % 20
            const dur = 5 + (i % 3) * 2
            const delay = (i * 1.5) % 4
            return {
                top: top + '%',
                transform: 'rotate(' + angle + 'deg)',
                animationDuration: dur + 's',
                animationDelay: delay + 's',
            }
        },
        cloudStyle(i) {
            const spots = [
                [8, 5], [50, 55], [22, 35], [65, 10], [35, 75],
                [12, 50], [55, 85],
            ]
            const p = spots[(i - 1) % spots.length]
            const w = 130 + (i * 40) % 120
            const h = Math.round(w * 0.3)
            const dur = 8 + (i * 3) % 8
            const delay = -((i * 1.8) % 6)
            return {
                top: p[0] + '%',
                left: p[1] + '%',
                width: w + 'px',
                height: h + 'px',
                animationDuration: dur + 's',
                animationDelay: delay + 's',
            }
        },
        fogStyle(i) {
            const top = -10 + (i * 28) % 80
            const dur = 8 + (i * 3) % 8
            const delay = (i * 2) % 5
            return {
                top: top + '%',
                animationDuration: dur + 's',
                animationDelay: delay + 's',
                animationDirection: i % 2 === 0 ? 'normal' : 'reverse',
            }
        },
        rippleStyle(i) {
            const left = 5 + ((i * 13.7 + i * i * 3.1) % 85)
            const dur = 1.8 + (i % 4) * 0.5
            const delay = (i * 0.42) % 3
            return {
                left: left + '%',
                animationDuration: dur + 's',
                animationDelay: delay + 's',
            }
        },
        snowMoundStyle(i) {
            const mounds = [
                { left: -5, width: 28, height: 14 },
                { left: 12, width: 24, height: 24 },
                { left: 28, width: 26, height: 18 },
                { left: 44, width: 22, height: 28 },
                { left: 60, width: 24, height: 20 },
                { left: 74, width: 22, height: 26 },
                { left: 90, width: 20, height: 16 },
                { left: 6, width: 18, height: 30 },
                { left: 52, width: 16, height: 32 },
                { left: 82, width: 18, height: 22 },
            ]
            const m = mounds[(i - 1) % mounds.length]
            return {
                left: m.left + '%',
                width: m.width + '%',
                height: m.height + 'px',
            }
        },
        starStyle(i) {
            const top = 3 + ((i * 31.7 + i * i * 3.1) % 90)
            const left = 2 + ((i * 37.3 + i * i * 2.7) % 94)
            const size = 2 + (i % 3)
            const dur = 1.5 + (i % 5) * 0.7
            const delay = (i * 0.37) % 4
            return {
                top: top + '%',
                left: left + '%',
                width: size + 'px',
                height: size + 'px',
                animationDuration: dur + 's',
                animationDelay: delay + 's',
            }
        },
    },
    watch: {
        sidebarOpen() { this.panelSaveLayoutDebounced() },
    },
    mounted() {
        this.$nextTick(() => {
            this.$watch('panelWidth', (w, old) => {
                if (!this.isCompact && w < 770 && old >= 770 && this.sidebarOpen) {
                    this.sidebarOpen = false
                }
            })
        })
        if (this.isCompact) {
            if (weatherService.state.locations.length) {
                this.mobileView = 'content'
            } else {
                this.mobileView = 'list'
            }
        }
    },
}
</script>
